import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Crown, Shield, Flame } from 'lucide-react';
import { PROFILE_DEFINITIONS } from '../utils/themeManager';

const PROFILE_ICONS = { Target, Crown, Shield, Flame };

const ProfileTransition = ({ profile, visible, onComplete }) => {
  const [phase, setPhase] = useState('scan'); // scan -> reveal -> fade

  const def = profile ? PROFILE_DEFINITIONS[profile] : null;
  const IconComponent = def ? (PROFILE_ICONS[def.icon] || Target) : Target;

  useEffect(() => {
    if (!visible || !def) { setPhase('scan'); return; }
    const t1 = setTimeout(() => setPhase('reveal'), 1200);
    const t2 = setTimeout(() => setPhase('fade'), 3800);
    const t3 = setTimeout(() => onComplete?.(), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [visible, def, onComplete]);

  // Play a subtle click sound
  useEffect(() => {
    if (!visible || !def) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) {
        console.warn('[Audio] AudioContext not available');
        return;
      }
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 880;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
      // Second tone (confirmation)
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = 1320;
        osc2.type = 'sine';
        gain2.gain.setValueAtTime(0.12, ctx.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
        osc2.start(ctx.currentTime);
        osc2.stop(ctx.currentTime + 0.4);
      }, 200);
    } catch (e) {
      // Audio not supported
    }
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && def && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          data-testid="profile-transition-overlay"
        >
          {/* Background */}
          <motion.div
            className={`absolute inset-0 bg-gradient-to-br ${def.colors.gradient}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: phase === 'fade' ? 0 : 0.95 }}
            transition={{ duration: 0.8 }}
          />

          {/* Scan line effect */}
          {phase === 'scan' && (
            <motion.div
              className="absolute left-0 right-0 h-1 bg-white/60"
              initial={{ top: '0%' }}
              animate={{ top: '100%' }}
              transition={{ duration: 1, ease: 'linear' }}
              style={{ boxShadow: '0 0 30px 10px rgba(255,255,255,0.3)' }}
            />
          )}

          {/* Content */}
          <div className="relative z-10 text-center px-6">
            {/* Icon */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: phase !== 'scan' ? 1 : 0.5, rotate: 0 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.3 }}
              className="mb-8"
            >
              <div
                className="w-28 h-28 mx-auto rounded-full flex items-center justify-center"
                style={{
                  background: `linear-gradient(135deg, ${def.colors.primary}, ${def.colors.accent})`,
                  boxShadow: `0 0 60px ${def.colors.primary}50`,
                }}
              >
                <IconComponent className="w-14 h-14 text-white" strokeWidth={2.5} />
              </div>
            </motion.div>

            {/* Profile name */}
            <motion.h1
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: phase !== 'scan' ? 0 : 40, opacity: phase !== 'scan' ? 1 : 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="font-heading text-5xl sm:text-6xl font-black uppercase tracking-tight text-white mb-4"
              style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
            >
              {def.label}
            </motion.h1>

            {/* Welcome message */}
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: phase === 'reveal' ? 0 : 20, opacity: phase === 'reveal' ? 1 : 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-white/90 text-lg sm:text-xl max-w-md mx-auto leading-relaxed"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
            >
              {def.message_fr}
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileTransition;
